<script>
import SearchQueryAnalyticsFilters from '@/components/page-user-analytics/SearchQueryAnalyticsFilters.vue';
import SearchQueryAnalyticsObject from '@/components/page-user-analytics/SearchQueryAnalyticsObject.vue';
import PaginationController from '@/components/base-components/PaginationController.vue';
import constants from '@/constants';

export default {
    components: {
        SearchQueryAnalyticsFilters,
        SearchQueryAnalyticsObject,
        PaginationController,
    },
    data() {
        return {
            sortColumns: {
                idSort: { field: 'id', direction: null },
                userIdSort: { field: 'user_id', direction: null },
                createdAtSort: { field: 'created_at', direction: 'desc' },
                waitingTimeSort: { field: 'waiting_time', direction: null },
            },
            savedScrollLeft: 0,
        };
    },
    computed: {
        analytics() {
            return this.$store.getters.SEARCH_QUERY_ANALYTICS;
        },
        isLoading() {
            return this.$store.getters.SEARCH_QUERY_ANALYTICS_LOADING;
        },
        pagination() {
            return this.$store.getters.SEARCH_QUERY_ANALYTICS_PAGINATION;
        },
        currentPage() {
            return this.$store.getters.SEARCH_QUERY_ANALYTICS_CURRENT_PAGE;
        },
        totalPages() {
            if (!this.pagination.perPage || this.pagination.perPage === 0) {
                return 1;
            }
            return Math.ceil(
                this.pagination.total / this.pagination.perPage
            );
        },
        maxPage() {
            return this.totalPages > 0 ? this.totalPages - 1 : 0;
        },
        displayRange() {
            const start =
                (this.currentPage - 1) * this.pagination.perPage + 1;
            const end = Math.min(
                this.currentPage * this.pagination.perPage,
                this.pagination.total
            );
            return `${start}-${end} of ${this.pagination.total}`;
        },
        paginationOptions() {
            return constants.SEARCH_QUERY_PAGINATION_OPTIONS;
        },
    },
    watch: {
        isLoading(newVal) {
            if (newVal === false) {
                // Data finished loading, restore scroll position
                this.$nextTick(() => {
                    const tableContainer = this.$el?.querySelector('.table_scroll_container');
                    if (tableContainer && this.savedScrollLeft !== null) {
                        tableContainer.scrollLeft = this.savedScrollLeft;
                    }
                });
            }
        },
    },
    methods: {
        saveScrollPosition() {
            const tableContainer = this.$el?.querySelector('.table_scroll_container');
            this.savedScrollLeft = tableContainer ? tableContainer.scrollLeft : 0;
        },
        handleSort(columnId) {
            const column = this.sortColumns[columnId];
            if (!column) return;

            this.saveScrollPosition();

            if (column.direction === null) {
                column.direction = 'asc';
            } else if (column.direction === 'asc') {
                column.direction = 'desc';
            } else {
                column.direction = null;
            }

            Object.keys(this.sortColumns).forEach((key) => {
                if (key !== columnId) {
                    this.sortColumns[key].direction = null;
                }
            });

            const sortBy = column.direction ? column.field : 'created_at';
            const sortOrder = column.direction || 'desc';

            this.$store.commit('SET_SEARCH_QUERY_ANALYTICS_SORT', {
                sortBy,
                sortOrder,
            });
            this.$store.commit('SET_SEARCH_QUERY_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadSearchQueryAnalytics');
        },
        getSortDirection(columnId) {
            return this.sortColumns[columnId]?.direction || null;
        },
        async changePerPage(event) {
            this.saveScrollPosition();

            const newPerPage = parseInt(event.target.value);
            this.$store.commit(
                'SET_SEARCH_QUERY_ANALYTICS_PER_PAGE',
                newPerPage
            );
            await this.$store.dispatch('loadSearchQueryAnalytics');
        },
        async handlePageChange(action) {
            this.saveScrollPosition();

            const pageActions = {
                first: () => 1,
                prev: () => Math.max(1, this.currentPage - 1),
                next: () => Math.min(this.totalPages, this.currentPage + 1),
                last: () => this.totalPages,
            };

            const newPage = pageActions[action]?.() ?? this.currentPage;
            await this.$store.dispatch('changeSearchQueryPage', newPage);
        },
        async refreshData() {
            this.saveScrollPosition();

            await this.$store.dispatch('loadSearchQueryAnalytics');
        },
    },
};
</script>

<template>
    <div class="analytics_list_container">
        <div class="right_header">
            <label>Search Queries ({{ pagination.total }})</label>
            <button
                @click="refreshData"
                :disabled="isLoading"
                class="refresh_button"
                :title="'Refresh data'"
            >
                ↻ Refresh
            </button>
        </div>

        <SearchQueryAnalyticsFilters />

        <div v-if="isLoading" class="loading_container">
            <p>Loading search queries...</p>
        </div>

        <div v-else-if="analytics.length === 0" class="empty_container">
            <p>No search query data found</p>
        </div>

        <div v-else>
            <div class="table_scroll_container">
                <table>
                    <thead>
                        <tr>
                            <th
                                @click="handleSort('idSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('idSort') !== null,
                                }"
                            >
                                ID
                                <span
                                    v-if="getSortDirection('idSort') === 'asc'"
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('idSort') === 'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('userIdSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('userIdSort') !== null,
                                }"
                            >
                                User ID
                                <span
                                    v-if="getSortDirection('userIdSort') === 'asc'"
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('userIdSort') === 'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th>Search Query</th>
                            <th>Tags</th>
                            <th>Providers</th>
                            <th>Games</th>
                            <th>Asset Type</th>
                            <th>File Format</th>
                            <th>Isolation</th>
                            <th>Proportions</th>
                            <th>Character</th>
                            <th>Season/Event</th>
                            <th>Theme</th>
                            <th>HQ Only</th>
                            <th>Custom Filters</th>
                            <th>Smart Filters</th>
                            <th>Results</th>
                            <th
                                @click="handleSort('waitingTimeSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('waitingTimeSort') !==
                                        null,
                                }"
                            >
                                Duration
                                <span
                                    v-if="
                                        getSortDirection('waitingTimeSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('waitingTimeSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('createdAtSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('createdAtSort') !==
                                        null,
                                }"
                            >
                                Created At
                                <span
                                    v-if="
                                        getSortDirection('createdAtSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('createdAtSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <SearchQueryAnalyticsObject
                            v-for="record in analytics"
                            :key="record.id"
                            :record="record"
                        />
                    </tbody>
                </table>
            </div>

            <div class="pagination_container">
                <div class="pagination_info">
                    <span>Showing {{ displayRange }} queries</span>

                    <div class="items_per_page">
                        <label>Items per page:</label>
                        <select
                            :value="pagination.perPage"
                            @change="changePerPage"
                            :disabled="isLoading"
                        >
                            <option
                                v-for="size in paginationOptions"
                                :key="size"
                                :value="size"
                            >
                                {{ size }}
                            </option>
                        </select>
                    </div>
                </div>

                <PaginationController
                    :currentPage="currentPage - 1"
                    :maxPage="maxPage"
                    @first="handlePageChange('first')"
                    @prev="handlePageChange('prev')"
                    @next="handlePageChange('next')"
                    @last="handlePageChange('last')"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.analytics_list_container {
    padding: 20px;
}

.right_header {
    margin-bottom: 20px;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.refresh_button {
    padding: 8px 16px;
    background: var(--main-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.refresh_button:hover:not(:disabled) {
    opacity: 0.9;
    transform: scale(1.02);
}

.refresh_button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.loading_container,
.empty_container {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
}

.table_scroll_container {
    overflow-x: auto;
    overflow-y: visible;
    margin: 20px 0;
    border: 1px solid var(--border-color);
    border-radius: 8px;
}

table {
    width: 100%;
    min-width: 2400px;
    border-collapse: collapse;
}

th {
    background: var(--background-hover);
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
}

th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
}

th.sortable:hover {
    background: #d1d5db;
    color: #1f2937;
}

th.sorted {
    background: #e5e7eb;
    color: #374151;
    font-weight: 700;
}

.sort_indicator {
    margin-left: 5px;
    font-size: 10px;
}

.pagination_container {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination_info {
    display: flex;
    align-items: center;
    gap: 20px;
    color: var(--text-color);
}

.items_per_page {
    display: flex;
    align-items: center;
    gap: 8px;
}

.items_per_page label {
    font-size: 14px;
    color: var(--text-color);
}

.items_per_page select {
    padding: 6px 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    cursor: pointer;
}

.items_per_page select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
